import {Injectable, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {EventParticipant} from "./models/EventParticipant";
import {AngularFireDatabase, QueryFn} from "angularfire2/database";
import {ScheduleService} from "../schedule-service";
import {UserPublicProfile, UserServiceProvider} from "../user-service/user-service";
import {UserConnection} from "./models/UserConnection";
import {FirebaseApp} from "angularfire2";
import {ConnectionRequest} from "./models/ConnectionRequest";
import {UserConversation} from "./models/UserConversation";
import {Message} from "./models/Message";
import * as firebase from 'firebase'
import {ConversationOverview} from "./models/ConversationOverview";
import {fromPromise} from "rxjs/observable/fromPromise";
import {of} from "rxjs/observable/of";
import {SessionService, UserSession} from "../session-service";
import {DataReference} from "../../shared/DataReference";
import {FeatureResponse} from "../../models/FeatureResponse";

@Injectable()
export class CommunicationProvider implements OnInit, OnDestroy {

  private _connectionRequestCount$: BehaviorSubject<number> = new BehaviorSubject(0);

  // participants
  private _eventParticipants$: BehaviorSubject<EventParticipant[]> = new BehaviorSubject([]);
  private _eventSearchableParticipantsSubscription: Subscription;
  private _cachedParticipants: {[key:string]:EventParticipant} = {};
  private _participantSubscriptions: Subscription[] = [];

  private _eventConnections$: Observable<UserConnection[]>;

  private _requestsSent$: Observable<ConnectionRequest[]>;
  private _requestsReceived$: Observable<ConnectionRequest[]>;

  private _eventConversations$: Observable<ConversationOverview[]>;
  totalUnreadConversationMessages$: Observable<number>;


  private _statusSub: Subscription;
  private _chatStatus$: BehaviorSubject<FeatureResponse> = new BehaviorSubject<FeatureResponse>({
    isEnabled: false,
    message: null
  });
  chatStatus$: Observable<FeatureResponse> = this._chatStatus$.asObservable();

  get connectionRequestCount$(): Observable<number> {
    return this._connectionRequestCount$.asObservable();
  }

  constructor(private af: AngularFireDatabase,
              private firebase: FirebaseApp,
              private userService: UserServiceProvider,
              private scheduleService: ScheduleService,
              private sessionService: SessionService) {
    console.log('Hello CommunicationProvider Provider');

    this.sessionService.userSession$
      .first()
      .subscribe(session => {
        this._watchPermissions(session);
      });

    this.chatStatus$
      .map(status => status.isEnabled)
      .distinctUntilChanged()
      .subscribe(isEnabled => {
        this.sessionService.userSession$
          .first()
          .subscribe(session => {
            this._loadEventParticipants(session.userId, session.eventId, isEnabled);
            this._loadEventConnections(session.userId, session.eventId, isEnabled);
            this._loadEventConversations(session.userId, session.eventId, isEnabled);
            this._loadRequestsSentToUsersAtEvent(session.userId, session.eventId, isEnabled);
            this._loadRequestsReceivedFromUsersAtEvent(session.userId, session.eventId, isEnabled);
          });
      });
  }

  private _watchPermissions(session: UserSession) {
    let participant$ : Observable<EventParticipant> = this.watchEventParticipant(session.eventId, session.userId, true);
    let profile$: Observable<UserPublicProfile> = this.userService.watchPublicProfile(session.userId);

    this._statusSub = Observable.combineLatest(participant$, profile$, this.sessionService.eventFeatures$,
      (participant, profile, features) => {

        console.log('inside stuff');
        console.log(participant);
        console.log(profile);
        console.log(features);

        let response: FeatureResponse = {
          isEnabled: false,
          message: 'Feature not enabled for account'
        };

        if (!participant) {
          console.log('No participant info found');
          return response;
        }

        if (participant.isBlocked) {
          console.log('User is blocked');
          return response;
        }

        if (participant.role < features.chat.minRoleRequired) {
          console.log('User does not meet minimum role requirements');
          return response;
        }

        if (!profile) {
          console.log('Profile is not complete');
          response.message = 'Profile is required to use feature';
          return response;
        }

        console.log('Checks passed. User should be allowed to use chat');
        response.isEnabled = true;
        response.message = null;
        return response;

      }).subscribe((status) => {
        this._chatStatus$.next(status);
    });

  }


  ngOnInit(): void {

  }

  getEventParticipants(includePrivate: boolean = false) : Observable<EventParticipant[]> {
    if (!includePrivate) {
      return this._eventParticipants$.asObservable().map(participants => participants.filter(p => p.accessibility >= EventParticipant.CHAT_ACCESSIBILITY_SEARCHABLE));
    } else {
      return this._eventParticipants$.asObservable();
    }
  }

  watchEventParticipant(eventId: string, userId:string, createIfNoneExists: boolean = false) : Observable<EventParticipant> {
    return this.af.object(`${DataReference.getEventParticipantsRef(eventId)}/${userId}`)
      .snapshotChanges()
      .switchMap(snap => {
        if (snap.payload.exists()) {
          return of(EventParticipant.fromAngularFireSnapshot(snap))
        } else {
          return fromPromise(this.getEventParticipant(eventId, userId, createIfNoneExists))
        }
      });
  }

  getEventParticipant(eventId: string, userId: string, createIfNoneExists: boolean = false) : Promise<EventParticipant> {
    return new Promise((resolve, reject) => {
      this.firebase.database().ref(`${DataReference.getEventParticipantsRef(eventId)}/${userId}`)
        .once(DataReference.Value, snap => {

          if (snap.exists()) {
            resolve(EventParticipant.fromSnapshot(snap));
          } else {

            if (createIfNoneExists) {

              this.sessionService.eventFeatures$
                .first()
                .subscribe(features => {
                  let eventParticipant: EventParticipant = new EventParticipant(
                    userId,
                    EventParticipant.DEFAULT_ROLE.id,
                    EventParticipant.DEFAULT_ROLE.name,
                    features.chat.defaultAccess
                  );

                  this._createEventParticipant(eventId, eventParticipant).then((participant) => {
                    resolve(participant);
                  }, err => {
                    console.error(err);
                    reject('Error getting profile');
                  });
                });

            } else {
              resolve(null);
            }
          }

      }, err => {
        console.error(err);
        reject('Error loading profile');
      });
    });
  }


  // region initializing helpers
  private _loadEventParticipants(userId: string, eventId: string, isAllowed: boolean) {
    if (isAllowed) {
      this._eventSearchableParticipantsSubscription = this.af.list(DataReference.getEventParticipantsRef(eventId),
        ref => ref.orderByChild(DataReference.CHAT_ACCESSIBILITY)
          .startAt(EventParticipant.CHAT_ACCESSIBILITY_PRIVATE))
        .snapshotChanges()
        .map(data =>
          EventParticipant.fromAngularFireSnapshots(data)
        ).subscribe(participants => {
          console.log('updated participants');

          for (let participant of participants) {
            if (this._cachedParticipants[participant.id]) {
              continue;
            }

            // add participant to cache
            this._cachedParticipants[participant.id] = participant;

            // watch public profiles
            let profileSub: Subscription = this.userService.watchPublicProfile(participant.id)
              .subscribe(profile => {
                if (profile) {
                  this._cachedParticipants[participant.id].profile = profile;
                  this._postParticipantsUpdate();
                } else {
                  console.warn('Profile not found');
                }
              });
            this._participantSubscriptions.push(profileSub);
          }
        });
    } else {
      this._cachedParticipants = {};
      this._clearLoadingSubscriptions();
    }
  }

  private _postParticipantsUpdate() {
    this._eventParticipants$.next(Object.keys(this._cachedParticipants)
      .map(id => this._cachedParticipants[id])
      .filter(participant => !!participant.profile));
  }

  private _loadEventConnections(userId: string, eventId: string, isAllowed: boolean) {

    if (isAllowed) {

      let allConnections$ = this.af.list(DataReference.getAllConnectionsForUserRef(userId))
        .snapshotChanges()
        .map(UserConnection.fromAngularFireSnapshots);

      // we only care about connections in the event
      this._eventConnections$ = Observable.combineLatest(allConnections$, this._eventParticipants$, (allConnections, participants) =>
        allConnections.map(connection => {
          connection.participant = participants.find(participant => participant.id == connection.id);
          return connection;
        }).filter(connection => !!connection.participant)
      );
    } else {
      this._eventConnections$ = null;
    }

  }

  private _loadRequestsSentToUsersAtEvent(userId: string, eventId: string, isAllowed: boolean) {
    if (isAllowed) {
      this._requestsSent$ = this.af.list(DataReference.getConnectionRequestsSentForUser(userId))
        .snapshotChanges()
        .map(ConnectionRequest.fromAngularFireSnapshots);
    } else {
      this._requestsSent$ = null;
    }
  }

  private _loadRequestsReceivedFromUsersAtEvent(userId: string, eventId: string, isEnabled: boolean) {
    if (isEnabled) {
      this._requestsReceived$ = this.af.list(DataReference.getConnectionRequestsReceivedForUser(userId))
        .snapshotChanges()
        .map(ConnectionRequest.fromAngularFireSnapshots);
    } else {
      this._requestsReceived$ = null;
    }
  }

  // endregion

  getEventConnections() : Observable<UserConnection[]> {
    return this._eventConnections$;
  }

  getRequestsSentFromUser() : Observable<ConnectionRequest[]> {
    return this._requestsSent$;
  }

  getRequestsReceived() : Observable<ConnectionRequest[]> {
    return this._requestsReceived$;
  }

  private _loadEventConversations(userId: string, eventId: string, isAllowed) {
    let conversations$: Observable<ConversationOverview[]> = this.af.list(DataReference.getEventConversationsForUser(eventId, userId))
      .snapshotChanges()
      .map(snapshots => snapshots.map(snap => this._getConversationById(snap.key)))
      .flatMap(obs => Observable.combineLatest(obs));

    let userConversations$: Observable<UserConversation[]> = conversations$
      .map(conversations => conversations.map(conversation => this._getUserConversation(conversation.id)))
      .flatMap(obs => Observable.combineLatest(obs));

    this._eventConversations$ = Observable.combineLatest(
      conversations$,
      this.getEventParticipants(),
      this.chatStatus$.map(s => s.isEnabled).distinctUntilChanged(),
      userConversations$,
      (conversations, eventParticipants, isEnabled, userConversations) => {
      console.log(conversations);

      if (!isEnabled) {
        return [];
      }

      conversations.forEach((conversation) => {
        let participants: EventParticipant[] = [];
        for (let participantId of conversation.participantIds) {
          let participant = eventParticipants.find(p => p.id == participantId);
          if (participant) {
            participants.push(participant);
          }
        }
        conversation.participants = participants;

        let userConversation: UserConversation = userConversations.find(c => c.id == conversation.id);
        if (userConversation) {
          conversation.unreadCount = userConversation.unread;
          conversation.status = userConversation.status;
        }
      });

      //console.log(conversations);
      return conversations;
    });

    this.totalUnreadConversationMessages$ = this._eventConversations$
      .map(conversations => conversations.map(
        conversation => conversation.unreadCount)
        .reduce((acc, val) => acc + val, 0));
  }

  private _getUserConversation(conversationId: string) : Observable<UserConversation> {
    return this.af.list(DataReference.getConversationsForUser(this.sessionService.userId), ref => ref.orderByChild('conversationId').equalTo(conversationId))
      .snapshotChanges()
      .map(UserConversation.fromAngularFireSnapshots)
      .map(items => items && items.length > 0 ? items[0] : null);
  }

  private _getConversationById(conversationId: string) : Observable<ConversationOverview> {
    return this.af.object(`${DataReference.getConversationsRef()}/${conversationId}`)
      .snapshotChanges()
      .filter(x => x.payload.exists())
      .map(ConversationOverview.fromAngularFireSnapshot);
  }

  private _clearLoadingSubscriptions() {
    if (this._eventSearchableParticipantsSubscription) {
      this._eventSearchableParticipantsSubscription.unsubscribe();
    }
    for (let sub of this._participantSubscriptions) {
      sub.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this._clearLoadingSubscriptions();
    if (this._statusSub) {
      this._statusSub.unsubscribe();
    }
  }

  private _createEventParticipant(eventId: string, participant: EventParticipant) : Promise<EventParticipant> {
    return new Promise((resolve, reject) => {

      let userId = this.sessionService.userId;
      participant.id = null;

      this.firebase.database().ref(DataReference.getEventParticipantsRef(eventId)).child(userId).update(participant)
        .then((err) => {
          if (err) {
            console.error(err);
            reject('Error creating profile');
          } else {
            participant.id = userId;
            resolve(participant);
          }
        });

    });
  }



  getConversationInfoForWithParticipant(participantId: string) : Promise<UserConversation> {
    return new Promise<UserConversation>((resolve, reject) => {
      let userId: string = this.sessionService.userId;

      this.firebase.database().ref(DataReference.getConversationWithParticipant(userId, participantId))
        .once(DataReference.Value).then(snap => {
        if (snap.exists()) {
          resolve(UserConversation.fromSnapshot(snap.key, snap.val()));
        } else {
          resolve(null);
        }
      }, err => {
          console.error(err);
          reject('Error loading conversation');
      });
    });
  }

  getConversationMessages(conversationId, startAtTimeStamp: number, maxMessages: number = 2) : Promise<Message[]> {

    return new Promise<Message[]>((resolve, reject) => {

      this.af.database.ref(DataReference.getMessagesForConversation(conversationId))
        .orderByChild(DataReference.TIMESTAMP).endAt(startAtTimeStamp).limitToLast(maxMessages)
        .once(DataReference.Value)
        .then(snaps => {
          console.log('got some snaphots');
          let messages: Message[] = [];
          if (snaps.hasChildren()) {
            snaps.forEach(snap => {
              messages.push(Message.fromSnapshot(snap.key, snap.val()));
            });
          }
          console.log(messages);
          resolve(messages);
        }, err => {
          console.error(err);
          reject('Error loading messages');
        });

    });

  }


  watchConversationMessages(conversationId: string, startAtTimeStamp: number = null, maxMessages: number = 2) : Observable<Message[]> {
    let query: QueryFn;
    if (startAtTimeStamp) {
      query = (ref) => ref.orderByChild(DataReference.TIMESTAMP).limitToLast(maxMessages).startAt(startAtTimeStamp);
    } else {
      query = (ref) => ref.orderByChild(DataReference.TIMESTAMP).limitToLast(maxMessages);
    }

    return this.af.list(DataReference.getMessagesForConversation(conversationId), query)
      .snapshotChanges()
      .map(Message.fromAngularFireSnapshots);
  }

  watchConversationWithUser(participantId: string) : Observable<UserConversation> {
    let userId: string = this.sessionService.userId;

    console.log(DataReference.getConversationWithParticipant(userId, participantId));
    return this.af.object(DataReference.getConversationWithParticipant(userId, participantId))
      .snapshotChanges()
      .map(snap => {
        return snap.payload.exists() ? UserConversation.fromSnapshot(snap.key, snap.payload.val()) : null;
      });
  }

  sendMessageToParticipant(message: Message, participantId: string) : Promise<void> {
    return new Promise<void>((resolve, reject) => {

      message.timestamp = firebase.database.ServerValue.TIMESTAMP;

      let data: {[key:string]:any} = {};
      let conversationId: string = message.conversationId;

      if (!conversationId) {
        this.getConversationInfoForWithParticipant(participantId).then(conversation => {

          if (conversation) {
            message.conversationId = conversation.id;
          } else {
            conversationId = this.firebase.database().ref(DataReference.getConversationsRef()).push().key;
            message.conversationId = conversationId;

            // conversation
            data[`${DataReference.getConversationsRef()}/${conversationId}`] = {
              users: {
                [message.sender]: true,
                [participantId]: true
              },
              status: UserConnection.CONNECTION_REQUEST_STATUS_OK
            };


            // user objects
            let conversationObject: any = {
              conversationId: conversationId,
              status: UserConnection.CONNECTION_REQUEST_STATUS_OK,
              unread: 0
            };

            data[DataReference.getConversationWithParticipant(message.sender, participantId)] = conversationObject;
            data[DataReference.getConversationWithParticipant(participantId, message.sender)] = conversationObject;
          }

          this._doSendMessage(participantId, message, data).then(() => {
            resolve();
          }, err => {
            reject(err);
          });

        });
      } else {
        this._doSendMessage(participantId, message).then(() => {
          resolve();
        }, err => {
          reject(err);
        });
      }

    });
  }

  private _doSendMessage(participantId: string, message: Message, data: {[key:string]: any} = {}) : Promise<void> {
    return new Promise<void>((resolve, reject) => {

      message.timestamp = firebase.database.ServerValue.TIMESTAMP;

      // messages
      let conversationId: string = message.conversationId;
      let messageId = this.firebase.database().ref(DataReference.getConversationMessagesRef()).push().key;

      data[DataReference.getMessageForConversation(conversationId, messageId)] = message;

      // event objects
      this.sessionService.userSession$
        .first()
        .withLatestFrom(this.sessionService.publicProfile$)
        .subscribe(([session, profile]) => {

          // set up event conversation for both users
          data[`${DataReference.getEventConversationsForUser(session.eventId, session.userId)}/${conversationId}`] = true;
          data[`${DataReference.getEventConversationsForUser(session.eventId, participantId)}/${conversationId}`] = true;

          // server processing
          let serverProcessing: any = Object.assign({}, message);
          serverProcessing['recipient'] = participantId;
          serverProcessing['senderName'] = profile.displayName;

          data[DataReference.getServerProcessingMessage(session.eventId, messageId)] = serverProcessing;

          console.log(data);

          this.firebase.database().ref().update(data, err => {
            if (err) {
              console.error(err);
              reject('Error sending message');
            } else {
              resolve();
            }
          });
        }, err => {
          console.error(err);
          reject('Error sending message');
        });
    });
  }

  getEventConversations() : Observable<ConversationOverview[]> {
    return this._eventConversations$;
  }

  setVisibility(eventId: string, userId: string, isVisible: boolean) : Promise<void> {
    return new Promise<void>((resolve, reject) => {

      let data = {};
      data[`${DataReference.getEventParticipantsRef(eventId)}/${userId}/${DataReference.CHAT_ACCESSIBILITY}`] =
        isVisible ? EventParticipant.CHAT_ACCESSIBILITY_SEARCHABLE : EventParticipant.CHAT_ACCESSIBILITY_DEFAULT;

      this.firebase.database().ref().update(data, err => {
        if (err) {
          console.error(err);
          reject('Error updating profile');
        } else {
          resolve();
        }
      });

    });
  }

  clearUnreadMessages(userId: string, participantId: string) : Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!userId || !participantId) {
        console.error('invalid parameters');
        reject('Error clearing unread messages');
        return;
      }

      let data: {[key:string]:any} = {};
      data[`${DataReference.getConversationWithParticipant(userId, participantId)}/unread`] = 0;

      this.firebase.database().ref().update(data, err => {
        if (err) {
          console.error(err);
          reject('Error clearing unread messages');
        } else {
          resolve();
        }
      });
    });
  }
}




// WEBPACK FOOTER //
// ./src/providers/communication/communication.ts